// Catering.js
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import EmptyCatering from "./EmptyCatering";
import AddDish from "./Dish/AddDish";
import EditDish from "./Dish/EditDish";
import Loader from "../Loader";
import "./addons.css";
import { useNavigate } from "react-router-dom";
import DeleteDish from "./Dish/DeleteDish";
import { API_URL } from "../../utils/config";
import AuthContext from "../../contexts/AuthProvider";
import { useContext } from "react";
import { getUserRole, USER_ROLES } from "../../utils/UsersPermissions";
const baseUrl = API_URL;

const Catering = () => {
  const { t } = useTranslation("catering");
  const navigate = useNavigate();
  const [caterings, setCaterings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("allTypes");
  const [action, setAction] = useState(null);
  const [selectedCatering, setSelectedCatering] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const actionsDropdownRef = useRef(null);
  const { isAuthenticated } = useContext(AuthContext);

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const fetchCatering = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/caterings`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        navigate("/login");
        throw new Error(t("errors.unauthorized"));
      }
      if (!response.ok) {
        throw new Error(t("errors.fetchFailed"));
      }

      const invitation = await response.json();
      return invitation.data;
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");

        navigate("/");
      }
      return [];
    }
  };

  useEffect(() => {
    const getCaterings = async () => {
      setLoading(true);
      const catering = await fetchCatering();
      setCaterings(catering);
      setLoading(false);
    };
    getCaterings();
  }, []);

  const transformedCaterings = caterings.map((catering) => ({
    id: catering.id,
    name: catering.dish_name || t("unknown"),
    cost: catering.cost || t("unknown"),
    cuisine_type: catering.cuisine_type || t("unknown"),
    venue_id: catering.venue_id,
    ingredients: catering.ingredients || [],
  }));

  const filteredCaterings = transformedCaterings.filter((catering) => {
    const matchesSearch =
      searchTerm === "" ||
      (catering.name?.toLowerCase() ?? "").includes(searchTerm.toLowerCase()) ||
      (catering.cuisine_type?.toLowerCase() ?? "").includes(
        searchTerm.toLowerCase()
      );

    const matchesStatus =
      statusFilter === "allTypes" || catering.cuisine_type === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleDropdown = (id) => {
    setActiveDropdown((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        actionsDropdownRef.current &&
        !actionsDropdownRef.current.contains(e.target) &&
        !e.target.closest(".details-dropdown")
      ) {
        setActiveDropdown(null);
      }
    };
    if (activeDropdown !== null) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [activeDropdown]);

  const handleAction = (act, id, venue_id) => {
    setAction(act);
    setSelectedCatering(id);
    setSelectedVenue(venue_id);
  };

  const refreshCatering = async () => {
    const data = await fetchCatering();
    setCaterings(data || []);
  };

  const closeForm = () => {
    refreshCatering();
    setAction(null);
  };

  if (loading) {
    return (
      <div className="addons-loader-container">
        <Loader />
      </div>
    );
  }

  if (caterings.length > 0) {
    return (
      <div className="addons-container">
        {/* Search and Controls */}
        <div className="addon-actions">
          <div className="controls-row">
            <div className="search-box">
              <img src="/search-normal.svg" alt="search" />
              <input
                type="text"
                placeholder={t("searchPlaceholder")}
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="controls-actions">
              <div className="status-select">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="allTypes">{t("statusFilter.allTypes")}</option>
                  <option value="international">
                    {t("statusFilter.international")}
                  </option>
                  <option value="local">{t("statusFilter.local")}</option>
                  <option value="mediterranean">
                    {t("statusFilter.mediterranean")}
                  </option>
                  <option value="asian">{t("statusFilter.asian")}</option>
                  <option value="vegetarian">
                    {t("statusFilter.vegetarian")}
                  </option>
                </select>
              </div>
              {/* Hide add catering button for viewers */}
              {getUserRole() !== USER_ROLES.VIEWER && (
                <button onClick={() => setAction("add")} className="add-addon">
                  <img src="/add.svg" alt="add" />
                  {t("buttons.addCuisine")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Caterings Table */}
        <div className="addon-table table-wrapper">
          <table className="addons-table">
            <thead>
              <tr>
                <th>{t("tableHeaders.dishName")}</th>
                <th>{t("tableHeaders.cuisineType")}</th>
                <th>{t("tableHeaders.ingredients")}</th>
                <th>{t("tableHeaders.cost")}</th>
                {/* Hide action header for viewers */}
                {getUserRole() !== USER_ROLES.VIEWER && (
                  <th>{t("tableHeaders.action")}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredCaterings.length > 0 ? (
                filteredCaterings.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>
                      {t(`statusFilter.${c.cuisine_type}`, {
                        defaultValue: c.cuisine_type,
                      })}
                    </td>
                    <td>
                      {c.ingredients.map((ing, i) => (
                        <span key={i}>
                          {ing}
                          {i < c.ingredients.length - 1 && ", "}
                        </span>
                      ))}
                    </td>
                    <td>
                      {c.cost} {t("currency")}
                    </td>
                    {/* Hide action buttons for viewers */}
                    {getUserRole() !== USER_ROLES.VIEWER && (
                      <td>
                        <button
                          onClick={() => handleDropdown(c.id)}
                          className="action-details"
                        >
                          <img src="/more-circle.svg" alt="details" />
                          {activeDropdown === c.id && (
                            <ul
                              className="details-dropdown"
                              ref={actionsDropdownRef}
                            >
                              <li>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleAction("edit", c.id, c.venue_id)
                                  }
                                >
                                  {t("buttons.edit")}
                                </button>
                              </li>
                              <li>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleAction("delete", c.id, c.venue_id)
                                  }
                                >
                                  {t("buttons.delete")}
                                </button>
                              </li>
                            </ul>
                          )}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={getUserRole() !== USER_ROLES.VIEWER ? "5" : "4"}
                    className="no-data"
                  >
                    {t("noData")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="addon-mobile-table">
          {filteredCaterings.length > 0 ? (
            <ul>
              {filteredCaterings.map((c) => (
                <li key={c.id} className="addon-card">
                  <div className="addon-card-content">
                    <div className="addon-main-info">
                      <span>{c.name}</span>
                      <span className="addon-cost">
                        {c.cost} {t("currency")}
                      </span>
                    </div>
                    <div className="addon-info">
                      {t(`statusFilter.${c.cuisine_type}`, {
                        defaultValue: c.cuisine_type,
                      })}
                      ,{" "}
                      {c.ingredients.map((ing, i) => (
                        <span key={i}>
                          {ing}
                          {i < c.ingredients.length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div>
              <div className="no-data">{t("noData")}</div>
            </div>
          )}
        </div>

        {action === "add" && <AddDish onClose={closeForm} />}
        {action === "edit" && (
          <EditDish
            onClose={closeForm}
            catering_id={selectedCatering}
            venue_id={selectedVenue}
          />
        )}
        {action === "delete" && (
          <DeleteDish
            onClose={closeForm}
            catering_id={selectedCatering}
            venue_id={selectedVenue}
          />
        )}
      </div>
    );
  } else {
    return (
      <>
        <EmptyCatering addDish={() => setAction("add")} />
        {action === "add" && <AddDish onClose={closeForm} />}
      </>
    );
  }
};

export default Catering;
